import DealDriverService from 'services/dealDriver/dealDriver.service';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
	updateFilters,
	updateSorting,
	updateSelectedAccounts,
	updateSelectedDealerships,
	updateSelectedUsers,
	updateSelectedScopeableEntityName,
	updateSelectedScopeableEntityType,
} from './slice';
import { createServiceAsyncThunkFactory } from 'store/helpers/createServiceAsyncThunkFactory';
import { persistNewDealIds } from './helpers';
import { parseSelectedIds } from 'helpers/deal_driver';
import { SCOPEABLE_ENTITY_TYPES } from 'components/DealDriver/constants';

const createDealDriverAsyncThunk = createServiceAsyncThunkFactory();
const DEFAULT_SUBMIT_ERROR_MESSAGE = 'Failed to submit this request. Please try again.';

const getDiscountSubmitErrorPayload = (response = {}) => {
	const directorIdFieldError = response?.error_details?.fields?.director_id;
	const directorIdError =
		typeof directorIdFieldError === 'string' ? directorIdFieldError
		: Array.isArray(directorIdFieldError) ? directorIdFieldError.find((error) => typeof error === 'string')
		: undefined;

	return directorIdError ? { directorIdError } : DEFAULT_SUBMIT_ERROR_MESSAGE;
};

export const getAllBidSales = createDealDriverAsyncThunk(
	'dealDriver/getAllBidSales',
	async ({ page = 1 } = {}, { getState }) => {
		const {
			dealDriver: { filters, sorting },
		} = getState();
		const response = await DealDriverService.instance.getAll({ page, filters, sorting });
		const { data, metadata } = response?.data || {};
		const bidSales = data || [];

		return { bidSales, metadata };
	},
	'Failed to load bid sales. Please go back and try again.',
);

export const getScopeableEntities = createDealDriverAsyncThunk(
	'dealDriver/getScopeableEntities',
	async ({ page = 1 } = {}, { getState }) => {
		const { dealDriver } = getState();
		const { name } = dealDriver.scopeableEntities.formData;
		const response = await DealDriverService.instance.getScopeableEntities({ page, name });
		const { data, metadata } = response?.data || {};
		return { scopeableEntities: data, metadata };
	},
	'Failed to load deal workers. Please refresh and try again.',
);

export const getAccounts = createDealDriverAsyncThunk(
	'dealDriver/getAccounts',
	async ({ page = 1 } = {}, { getState }) => {
		const { dealDriver } = getState();
		const { name } = dealDriver.accounts.formData;
		const { selectedDealerships } = dealDriver.dealerships;
		const dealershipIds = parseSelectedIds(selectedDealerships);
		const { selectedUsers } = dealDriver.users;
		const userIds = parseSelectedIds(selectedUsers);
		const response = await DealDriverService.instance.getAccounts({ page, name, dealershipIds, userIds });
		const { data, metadata } = response?.data || {};
		return { accounts: data, metadata };
	},
	'Failed to load accounts. Please refresh and try again.',
);

export const getUsers = createDealDriverAsyncThunk(
	'dealDriver/getUsers',
	async ({ page = 1 } = {}, { getState }) => {
		const { dealDriver } = getState();
		const { name } = dealDriver.users.formData;
		const { selectedDealerships } = dealDriver.dealerships;
		const { selectedAccounts } = dealDriver.accounts;
		const dealershipIds = parseSelectedIds(selectedDealerships);
		const accountIds = parseSelectedIds(selectedAccounts);
		const response = await DealDriverService.instance.getUsers({ page, name, dealershipIds, accountIds });
		const { data, metadata } = response?.data || {};
		return { users: data, metadata };
	},
	'Failed to load users. Please refresh and try again.',
);

export const getDealerships = createDealDriverAsyncThunk(
	'dealDriver/getDealerships',
	async ({ includeIdAndUser = false } = {}, { getState }) => {
		const { dealDriver } = getState();
		const { name } = dealDriver.dealerships.formData;
		const { selectedAccounts } = dealDriver.accounts;
		const accountIds = parseSelectedIds(selectedAccounts);
		const { selectedUsers } = dealDriver.users;
		const userIds = parseSelectedIds(selectedUsers);
		const response = await DealDriverService.instance.getDealerships(name, accountIds, includeIdAndUser, userIds);
		const { data, metadata } = response?.data || {};
		return { dealerships: data, metadata };
	},
	'Failed to load dealerships. Please refresh and try again.',
);

export const getSelectedAccountsDetails = createDealDriverAsyncThunk(
	'dealDriver/getSelectedAccountsDetails',
	async (ids, { dispatch }) => {
		const response = await DealDriverService.instance.getAccountsDetails(ids);
		const { data } = response?.data || {};
		dispatch(updateSelectedAccounts(data));
	},
	'Failed to load accounts. Please refresh and try again.',
);

export const getSelectedUsersDetails = createDealDriverAsyncThunk(
	'dealDriver/getSelectedUsersDetails',
	async (ids, { dispatch }) => {
		const response = await DealDriverService.instance.getUsersDetails(ids);
		const { data } = response?.data || {};
		dispatch(updateSelectedUsers(data));
	},
	'Failed to load users. Please refresh and try again.',
);

export const getSelectedDealershipsDetails = createDealDriverAsyncThunk(
	'dealDriver/getSelectedDealershipsDetails',
	async (ids, { dispatch }) => {
		const response = await DealDriverService.instance.getDealershipsDetails(ids);
		const { data } = response?.data || {};
		dispatch(updateSelectedDealerships(data));
	},
	'Failed to load dealerships. Please refresh and try again.',
);

export const getClosedMarketplaces = createDealDriverAsyncThunk(
	'dealDriver/getClosedMarketplaces',
	async () => {
		const response = await DealDriverService.instance.getClosedMarketplaces();
		const { data } = response?.data || {};
		return { closedMarketplaces: data };
	},
	'Failed to load closed marketplaces. Please refresh and try again.',
);

export const getAllBidSalesDetails = createDealDriverAsyncThunk(
	'dealDriver/getAllBidSalesDetails',
	async ({ bidSalesIds } = {}) => {
		const response = await DealDriverService.instance.getAllBidSalesDetails(bidSalesIds);
		const { data } = response?.data || {};
		return { bidSales: data };
	},
);

export const createDiscountApprovalRequest = createDealDriverAsyncThunk(
	'dealDriver/createDiscountApprovalRequest',
	async ({ bidSaleId, approvalRequest } = {}, thunkAPI) => {
		const response = await DealDriverService.instance.createApprovalRequest(bidSaleId, approvalRequest);
		const responseData = response?.data;

		if (responseData?.success === false) {
			return thunkAPI.rejectWithValue(getDiscountSubmitErrorPayload(responseData));
		}

		return {
			bidSaleId,
			responseData,
		};
	},
	(error) => getDiscountSubmitErrorPayload(error?.error),
);

export const updateFiltersAndFetchBidSales = createAsyncThunk(
	'dealDriver/updateFiltersAndFetchBidSales',
	async (newFilters, { dispatch }) => {
		await dispatch(updateFilters(newFilters || {}));

		dispatch(getAllBidSales({ page: 1 }));
	},
);

export const updateSortingAndFetchBidSales = createAsyncThunk(
	'dealDriver/updateSortingAndFetchBidSales',
	async (newSorting, { dispatch }) => {
		await dispatch(updateSorting(newSorting));

		dispatch(getAllBidSales({ page: 1 }));
	},
);

export const updatePageAndFetchBidSales = createAsyncThunk(
	'dealDriver/updatePageAndFetchBidSales',
	async (page, { dispatch }) => {
		dispatch(getAllBidSales({ page }));
	},
);

export const getSearchRefreshAssessment = createAsyncThunk(
	'dealDriver/getSearchRefreshAssessment',
	async ({ uniqueIds, dealWorkerId }, { getState }) => {
		const {
			dealDriver: { filters, sorting, metadata },
		} = getState();

		const response = await DealDriverService.instance.getSearchRefreshAssessment({
			page: metadata.page || 1,
			filters,
			sorting,
			bidSaleIds: uniqueIds,
		});

		const newIdsData = response?.data?.data || [];

		persistNewDealIds(dealWorkerId, newIdsData);

		return newIdsData;
	},
);

export const getDealWorkerDetails = createAsyncThunk(
	'dealDriver/getDealWorkerDetails',
	async ({ dealWorkerId }, { dispatch }) => {
		const response = await DealDriverService.instance.getDealWorkerDetails(dealWorkerId);
		const { data } = response?.data || {};

		if (!data?.name) return;

		dispatch(updateSelectedScopeableEntityName(data.name));
		dispatch(updateSelectedScopeableEntityType(SCOPEABLE_ENTITY_TYPES.ADMIN_USER));
	},
);
